import { jwtDecode } from 'jwt-decode';

import type {
  ClavePoliticaUsuario,
  InformacionUsuarioToken,
  JwtAutenticacionPayload,
} from 'tipos/autenticacion';
import { obtenerCookie, obtenerTokenDesdeCookies } from 'utilidades/cookies';

const convertirPoliticas = (
  payload: JwtAutenticacionPayload
): Record<ClavePoliticaUsuario, number | null> => {
  const politicas = {
    politicaVerTecnicosComdata: payload.politica_ver_tecnicos_comdata ?? null,
    politicaVerVistaUnidadesMoviles: payload.politica_ver_vista_unidades_moviles ?? null,
    politicaVerVigilantes: payload.politica_ver_vigilantes ?? null,
    politicaVerSma: payload.politica_ver_sma ?? null,
    politicaVerUsuarios: payload.politica_ver_usuarios ?? null,
    politicaVerSsei: payload.politica_ver_ssei ?? null,
    politicaVerTecnicosLoomis: payload.politica_ver_tecnicos_loomis ?? null,
  };

  const todasNulas = Object.values(politicas).every((valorActual) => valorActual === null);

  if (todasNulas) {
    return politicas;
  }

  return Object.entries(politicas).reduce((acumulador, [claveActual, valorActual]) => {
    acumulador[claveActual as ClavePoliticaUsuario] = valorActual === null ? 0 : valorActual;
    return acumulador;
  }, {} as Record<ClavePoliticaUsuario, number | null>);
};

export const parsearJwt = (token: string): JwtAutenticacionPayload => jwtDecode(token);

export const validarToken = (): boolean => {
  const token = obtenerTokenDesdeCookies();

  if (!token) {
    return false;
  }

  try {
    const tokenDecodificado = parsearJwt(token);
    const fechaActual = Math.floor(Date.now() / 1000);

    return typeof tokenDecodificado.exp === 'number' ? tokenDecodificado.exp >= fechaActual : true;
  } catch (error) {
    console.error('Token inválido:', error);
    return false;
  }
};

export const obtenerRoleDesdeToken = (): string | null => {
  const token = obtenerTokenDesdeCookies();

  if (!token) {
    return null;
  }

  try {
    const tokenDecodificado = parsearJwt(token);
    return tokenDecodificado.role ?? null;
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

export const obtenerInformacionUsuarioDesdeToken = (): InformacionUsuarioToken | null => {
  const token = obtenerTokenDesdeCookies();

  if (!token) {
    return null;
  }

  try {
    const tokenDecodificado = parsearJwt(token);
    const politicas = convertirPoliticas(tokenDecodificado);

    return {
      role: tokenDecodificado.role ?? null,
      cambioDePass: tokenDecodificado.cambio_de_pass ?? 0,
      codigoVerificacionActivo: tokenDecodificado.codigo_verificacion_activo ?? 0,
      fechaUltimoCambioPass: tokenDecodificado.fecha_ultimo_cambio_pass ?? null,
      userName: tokenDecodificado.userName ?? null,
      userId: tokenDecodificado.user_id ?? null,
      ...politicas,
    };
  } catch (error) {
    console.error('Error decoding token:', error);
    return null;
  }
};

export const obtenerRutaRedireccionPorPoliticas = (
  informacionUsuario: InformacionUsuarioToken | null
): string => {
  if (!informacionUsuario) {
    return '/login';
  }

  const politicasEnOrden: Array<[ClavePoliticaUsuario, string]> = [
    ['politicaVerVistaUnidadesMoviles', '/lista'],
    ['politicaVerVigilantes', '/vigilantes'],
    ['politicaVerTecnicosComdata', '/tecnicos'],
    ['politicaVerUsuarios', '/usuarios'],
    ['politicaVerSma', '/tecnicos-sma'],
    ['politicaVerSsei', '/tecnicos-ssei'],
    ['politicaVerTecnicosLoomis', '/tecnicos-loomis'],
  ];

  const todasNulas = politicasEnOrden.every(([claveActual]) => informacionUsuario[claveActual] === null);

  if (todasNulas) {
    return informacionUsuario.role === 'ADMIN' ? '/usuarios' : '/lista';
  }

  const rutaPermitida = politicasEnOrden.find(([claveActual]) => informacionUsuario[claveActual] === 1);
  return rutaPermitida?.[1] ?? '/login';
};

export const obtenerCookieUsuarioVerificado = (nombreUsuario: string): string | null =>
  obtenerCookie(`${nombreUsuario}cookie`);
